import { useState, useCallback } from 'react';
import { comensalApi } from '../api/comensalApi';
import { turnoApi } from '../api/turnoApi';
import { showErrorToast } from '../utils/errorHandler';

/**
 * Hook optimizado para el modal de Turno Detalle
 * Solo incluye las funciones necesarias sin cargar datos automáticamente
 */
export const useComensalesModal = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Maneja errores de validación (422)
   */
  const handleValidationError = useCallback((result) => {
    // FastAPI envuelve los errores HTTPException en 'detail'
    // Si result es el objeto completo de la respuesta, extraer detail
    let errorData = result.detail || result;

    // Si errorData es un array (FastAPI a veces envuelve en array), tomar el primer elemento
    if (Array.isArray(errorData) && errorData.length > 0) {
      errorData = errorData[0];
    }
    
    // Si errorData.message es un objeto anidado (puede pasar con FastAPI), extraer el mensaje real
    if (errorData && typeof errorData.message === 'object' && errorData.message !== null) {
      // Si message es un objeto con su propio message, usar ese
      if (errorData.message.message && typeof errorData.message.message === 'string') {
        errorData = {
          ...errorData,
          message: errorData.message.message
        };
      } else if (errorData.message.success !== undefined) {
        // Si message tiene success, es el objeto completo, usar message.message
        errorData = errorData.message;
      }
    }
    
    // Asegurar que message sea siempre un string
    const errorMessage = typeof errorData?.message === 'string' 
      ? errorData.message 
      : (errorData?.message?.message || "Error de validación");
    
    // Si es el error de múltiples comensales activos
    if (errorData?.error_type === 'MULTIPLE_ACTIVE_COMENSALES') {
      return {
        success: false,
        message: errorMessage,
        color: "orange",
        hasAccess: false,
        error_type: 'MULTIPLE_ACTIVE_COMENSALES',
        comensales: errorData?.comensales
      };
    }
    
    // Si es el error de documento duplicado en turno
    if (errorData?.error_type === 'DOCUMENTO_DUPLICADO') {
      return {
        success: false,
        message: errorMessage,
        color: "red",
        hasAccess: false,
        error_type: 'DOCUMENTO_DUPLICADO',
        registro_existente: errorData?.registro_existente
      };
    }
    
    // Otros errores de validación
    return {
      success: false,
      message: errorMessage,
      color: "red",
      hasAccess: false
    };
  }, []);

  /**
   * Valida el acceso de un comensal a un comedor específico
   * Devuelve los datos del comensal si tiene acceso válido, o null con mensaje de error
   * @param {string} numDoc - Número de documento del comensal
   * @param {number} proyectoId - ID del proyecto
   * @param {number} comedorId - ID del comedor
   * @param {number} tipoServicioAlimentacionId - ID del tipo de servicio de alimentación (obligatorio)
   */
  const validateComensalAccess = useCallback(async (numDoc, proyectoId, comedorId, tipoServicioAlimentacionId, fechaTurno = null) => {
    setLoading(true);
    setError(null);
    
    try {
      // Usar endpoint de Python (FastAPI)
      const response = await comensalApi.validateAccess(numDoc, proyectoId, comedorId, tipoServicioAlimentacionId, fechaTurno);
      
      const result = response.data;
      
      // Si es un 422 (validación), manejar como error de validación
      if (response.status === 422) {
        // FastAPI envuelve HTTPException en 'detail'
        // La estructura es: { detail: { success: false, message: "...", ... } }
        const errorData = result.detail || result;
        console.log('ℹ️ Validación 422 recibida:', errorData);
        return handleValidationError(errorData);
      }
      
      // Si es éxito (200-299)
      if (result.success) {
        const comensalWithAccess = {
          id: result.id,
          id_cliente: result.id_cliente,
          nombre: result.nombre,
          numero_documento_comedor: result.numero_documento_comedor,
          cliente: result.cliente,
          tipo_origen_trabajador: result.tipo_origen_trabajador,
          tiene_historial_duplicado: result.tiene_historial_duplicado,
          advertencia: result.advertencia,
          message: "Acceso Autorizado",
          color: "green",
          hasAccess: true
        };
        
        return comensalWithAccess;
      } else {
        // Verificar si es un error de múltiples comensales activos
        if (result.error_type === 'MULTIPLE_ACTIVE_COMENSALES') {
          return {
            success: false,
            message: result.message,
            color: "orange",
            hasAccess: false,
            error_type: 'MULTIPLE_ACTIVE_COMENSALES',
            comensales: result.comensales
          };
        }
        
        // Retornar objeto con información del error normal
        return {
          success: false,
          message: result.message || "Acceso denegado",
          color: "red",
          hasAccess: false
        };
      }
    } catch (err) {
      console.error("❌ Error al validar acceso del comensal:", err);
      
      // Manejo de errores de FastAPI (con 'detail')
      if (err.response) {
        // Si es un error 422 (validación)
        if (err.response.status === 422) {
          const errorData = err.response.data?.detail || err.response.data || {};
          return handleValidationError(errorData);
        }
        
        // Si es un error 500 (error interno del servidor)
        if (err.response.status === 500) {
          const errorData = err.response.data || {};
          return {
            success: false,
            message: errorData.message || "Error interno del servidor",
            color: "red",
            hasAccess: false
          };
        }
        
        // Otros errores del servidor
        return {
          success: false,
          message: `Error del servidor (${err.response.status})`,
          color: "red",
          hasAccess: false
        };
      }
      
      // Error de red (sin response)
      return {
        success: false,
        message: "Error de conexión con el servidor",
        color: "red",
        hasAccess: false
      };
    } finally {
      setLoading(false);
    }
    
    // COMENTADO: Código para PHP (Laravel) - rollback disponible si es necesario
    // try {
    //   const response = await comensalApi.validateAccess(numDoc, proyectoId, comedorId, tipoServicioAlimentacionId);
    //   
    //   // Validar que la respuesta tenga data
    //   if (!response || !response.data) {
    //     console.error("❌ Respuesta inválida del servidor:", response);
    //     return {
    //       success: false,
    //       message: "Error: Respuesta inválida del servidor",
    //       color: "red",
    //       hasAccess: false
    //     };
    //   }
    //   
    //   const result = response.data;
    //   
    //   // PHP Laravel retorna directamente el objeto, no envuelve en 'detail' como FastAPI
    //   // Caso 1: Éxito (200 con success: true)
    //   if (result.success === true) {
    //     // ... código comentado para PHP
    //   }
    //   // ... resto del código comentado para PHP
    // } catch (err) {
    //   // ... manejo de errores comentado para PHP
    // }
  }, [handleValidationError]);

  /**
   * Obtiene los turnos detalle de un turno cabecera específico
   * Si no se especifica limit, devuelve todos los registros
   * Devuelve los registros formateados para la tabla del modal
   */
  const fetchRecentTurnosDetalle = useCallback(async (idTurnoCabecera, limit = null) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await turnoApi.getAll({ idTurnoCabecera: idTurnoCabecera });
      
      const turnosDetalle = response.data;
      
      if (Array.isArray(turnosDetalle)) {
        
        // Ordenar por created_at descendente y aplicar límite solo si se especifica
        const sortedTurnos = turnosDetalle
          .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
        
        // Aplicar límite solo si se especifica
        const finalTurnos = limit ? sortedTurnos.slice(0, limit) : sortedTurnos;
        
        
        // Formatear los datos para que coincidan con la estructura esperada por el modal
        const formattedRecords = finalTurnos.map((turno, index) => {
          
          return {
            id: turno.id,
            nombre: turno.comensal?.nombre || `${turno.comensal?.nombres} ${turno.comensal?.apellidos}` || 'N/A',
            numero_documento_comedor: turno.comensal?.numero_documento_comedor || turno.comensal?.num_doc || 'N/A',
            cliente: turno.cliente?.nombre_cliente || 'N/A',
            timestamp: turno.created_at 
              ? new Date(turno.created_at).toLocaleTimeString('es-PE', { 
                  hour: '2-digit', 
                  minute: '2-digit', 
                  second: '2-digit' 
                })
              : new Date().toLocaleTimeString('es-PE', { 
                  hour: '2-digit', 
                  minute: '2-digit', 
                  second: '2-digit' 
                })
          };
        });
        
        
        return formattedRecords;
      }
      
      return [];
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al cargar registros recientes del turno');
      setError(errorMessage);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Obtiene solo los números de documento registrados en un turno (optimizado)
   * Mucho más rápido que fetchRecentTurnosDetalle para validación de duplicados
   */
  const fetchDocumentosRegistrados = useCallback(async (idTurnoCabecera) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await turnoApi.getDocumentosRegistrados(idTurnoCabecera);
      
      if (response.data.success) {
        return response.data.data; // Array de números de documento
      }
      
      return [];
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al cargar documentos registrados');
      setError(errorMessage);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Valida si un documento ya está registrado en un turno específico
   */
  const validateDocumentInTurnoApi = useCallback(async (numDoc, turnoId) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await comensalApi.validateDocumentInTurnoApi(numDoc, turnoId);
      
      const result = response.data;
      
      if (result.success) {
        return {
          success: true,
          message: result.message,
          available: true
        };
      } else {
        return {
          success: false,
          message: result.message,
          available: false
        };
      }
    } catch (err) {
      console.error("Error al validar documento en turno:", err);
      
      // Verificar si es un error 422 con documento duplicado
      if (err.response && err.response.status === 422) {
        const errorData = err.response.data;
        
        if (errorData.error_type === 'DOCUMENTO_DUPLICADO') {
          console.log('🚨 DOCUMENTO DUPLICADO EN TURNO');
          return {
            success: false,
            message: errorData.message,
            color: "red",
            available: false,
            error_type: 'DOCUMENTO_DUPLICADO',
            registro_existente: errorData.registro_existente
          };
        }
      }
      
      // Error genérico
      return {
        success: false,
        message: "Error al validar documento en turno",
        color: "red",
        available: false
      };
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    // Estado
    loading,
    error,
    
    // Funciones
    validateComensalAccess,
    validarAccesoComensalApi: validateComensalAccess, // Alias para compatibilidad
    validateDocumentInTurnoApi,
    fetchRecentTurnosDetalle,
    fetchDocumentosRegistrados, // Nueva función optimizada
    
    // Función para limpiar errores
    clearError: () => setError(null),
  };
};

export default useComensalesModal;